import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';

interface IndicationRow {
  id: number;
  name: string;
  cancer_type?: string | null;
  is_active: boolean;
  trial_count: number;
  last_updated_at: string | null;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const days = Math.floor((Date.now() - then) / 864e5);
  if (days < 1) return 'today';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/** Admin panel for emerging_disease_indications — the canonical indication list used by the
 * Trial Tracker's indication filter (and elsewhere). Lets superusers rename an indication or
 * merge duplicates/near-duplicates into one, migrating every 1emerging_trials.indication_codes
 * and emerging_landscapes.indication_id reference (mirrors ManageEmergingAssets.tsx). */
const ManageEmergingIndications: React.FC = () => {
  const [rows, setRows] = useState<IndicationRow[]>([]);
  const [cancerTypes, setCancerTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingCancerType, setEditingCancerType] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<Record<number, string>>({});

  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<number>>(new Set());
  const [mergeTargetId, setMergeTargetId] = useState<string>('');
  const [merging, setMerging] = useState(false);
  const [mergeMessage, setMergeMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, cts] = await Promise.all([
        apiService.fetchSuperuserEmergingIndications(),
        apiService.fetchCancerTypes().catch(() => []),
      ]);
      if (res?.success) setRows(res.indications || []);
      else setError(res?.error || 'Failed to load indications.');
      const names = Array.isArray(cts)
        ? cts.map((c: any) => c?.name || c?.id || c).filter(Boolean)
        : [];
      setCancerTypes(Array.from(new Set(names as string[])).sort());
    } catch {
      setError('Failed to load indications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q)
      || (r.cancer_type || '').toLowerCase().includes(q),
    );
  }, [rows, query]);

  const startEdit = (row: IndicationRow) => {
    setEditingId(row.id);
    setEditingName(row.name);
    setEditingCancerType(row.cancer_type || '');
  };

  const saveEdit = async (id: number) => {
    const name = editingName.trim();
    if (!name) return;
    setSavingId(id);
    setRowError((prev) => ({ ...prev, [id]: '' }));
    try {
      const cancer_type = editingCancerType.trim() || null;
      const res = await apiService.renameEmergingIndication({
        indication_id: id,
        indication_name: name,
        cancer_type,
      });
      if (res?.success) {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, name, cancer_type } : r)));
        setEditingId(null);
      } else {
        setRowError((prev) => ({ ...prev, [id]: res?.error || 'Rename failed.' }));
      }
    } catch (e: any) {
      setRowError((prev) => ({ ...prev, [id]: e?.response?.data?.error || 'Rename failed.' }));
    } finally {
      setSavingId(null);
    }
  };

  const toggleSource = (id: number) => {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const targetOptions = useMemo(
    () => rows.filter((r) => !selectedSourceIds.has(r.id)),
    [rows, selectedSourceIds],
  );

  const runMerge = async () => {
    const targetId = Number(mergeTargetId);
    if (!targetId || selectedSourceIds.size === 0) return;
    setMerging(true);
    setMergeMessage(null);
    try {
      const res = await apiService.mergeEmergingIndications({
        source_indication_ids: Array.from(selectedSourceIds),
        target_indication_id: targetId,
      });
      if (res?.success) {
        setMergeMessage(`Merged ${res.merged_count} indication(s) into the target.`);
        setSelectedSourceIds(new Set());
        setMergeTargetId('');
        await load();
      } else {
        setMergeMessage(res?.error || 'Merge failed.');
      }
    } catch (e: any) {
      setMergeMessage(e?.response?.data?.error || 'Merge failed.');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold text-slate-900">Emerging Indications</h1>
        <span className="text-xs text-slate-400">{rows.length} total</span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Canonical indication list used by the Trial Tracker's indication filter. Assign a SoC{' '}
        <span className="font-medium">cancer type</span> so the tracker can group sub-indications.
        Rename to fix wording; select two or more and merge to consolidate duplicates.
      </p>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search indications or cancer type…"
        className="w-full max-w-sm text-sm px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-indigo-400 mb-4"
      />

      {selectedSourceIds.size > 0 && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <span className="text-sm font-semibold text-indigo-700">{selectedSourceIds.size} selected</span>
          <span className="text-sm text-slate-500">merge into</span>
          <select
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
            className="text-sm px-2 py-1.5 border border-slate-200 rounded-md outline-none focus:border-indigo-400"
          >
            <option value="">Choose target…</option>
            {targetOptions.map((r) => (
              <option key={r.id} value={r.id}>{r.name} ({r.trial_count} trials)</option>
            ))}
          </select>
          <button
            onClick={runMerge}
            disabled={merging || !mergeTargetId}
            className="text-sm font-semibold px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {merging ? 'Merging…' : 'Merge'}
          </button>
          <button
            onClick={() => setSelectedSourceIds(new Set())}
            className="text-sm text-slate-400 hover:text-slate-600"
          >
            Cancel
          </button>
        </div>
      )}
      {mergeMessage && <div className="text-sm text-slate-600 mb-3">{mergeMessage}</div>}

      {loading ? (
        <div className="py-10 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      ) : error ? (
        <div className="text-sm text-rose-600">{error}</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-2 w-8" />
              <th className="py-2 pr-2">Name</th>
              <th className="py-2 pr-2 w-52">Cancer type (SoC)</th>
              <th className="py-2 pr-2 w-24">Trials</th>
              <th className="py-2 pr-2 w-28">Last updated</th>
              <th className="py-2 pr-2 w-20">Active</th>
              <th className="py-2 w-16" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2 pr-2">
                  <input
                    type="checkbox"
                    checked={selectedSourceIds.has(row.id)}
                    onChange={() => toggleSource(row.id)}
                  />
                </td>
                <td className="py-2 pr-2">
                  {editingId === row.id ? (
                    <div className="flex flex-col gap-1.5">
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(row.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="text-sm px-2 py-1 border border-indigo-300 rounded-md outline-none w-full"
                      />
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => saveEdit(row.id)}
                          disabled={savingId === row.id}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex-none"
                        >
                          Save
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-xs text-slate-400 hover:text-slate-600 flex-none">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(row)} className="text-slate-700 hover:text-indigo-600 text-left">
                      {row.name}
                    </button>
                  )}
                  {rowError[row.id] && <div className="text-xs text-rose-600 mt-0.5">{rowError[row.id]}</div>}
                </td>
                <td className="py-2 pr-2">
                  {editingId === row.id ? (
                    <select
                      value={editingCancerType}
                      onChange={(e) => setEditingCancerType(e.target.value)}
                      className="w-full text-xs px-2 py-1 border border-indigo-300 rounded-md outline-none"
                    >
                      <option value="">Uncategorized</option>
                      {cancerTypes.map((ct) => (
                        <option key={ct} value={ct}>{ct}</option>
                      ))}
                      {editingCancerType && !cancerTypes.includes(editingCancerType) && (
                        <option value={editingCancerType}>{editingCancerType}</option>
                      )}
                    </select>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(row)}
                      className="text-xs text-slate-500 hover:text-indigo-600 text-left"
                    >
                      {row.cancer_type || <span className="text-slate-300">—</span>}
                    </button>
                  )}
                </td>
                <td className="py-2 pr-2 text-slate-500">{row.trial_count}</td>
                <td className="py-2 pr-2 text-slate-400 text-xs">{relativeTime(row.last_updated_at)}</td>
                <td className="py-2 pr-2">
                  {row.is_active ? (
                    <span className="text-xs font-semibold text-emerald-600">Active</span>
                  ) : (
                    <span className="text-xs text-slate-400">Hidden</span>
                  )}
                </td>
                <td className="py-2" />
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ManageEmergingIndications;
